import { Temporal } from '@js-temporal/polyfill'

interface Work {
  id: string
  from: string
  to: string
}

interface WorkWithLane extends Work {
  lane: number
}

/**
 * 重複する予定にレーン(行)番号を割り当てる
 * Googleカレンダーと同様のアルゴリズムを使用
 *
 * @param works 予定のリスト
 * @returns レーン番号と最大重複数が割り当てられた予定のリスト
 */
export const assignLanesToWorks = <T extends Work>(
  works: readonly T[],
): Array<T & { lane: number; maxOverlapCount: number }> => {
  if (works.length === 0) return []

  // 開始時間でソート（同じ場合は終了時間が遅い順）
  const sortedWorks = [...works]
    .map((work) => ({
      work,
      fromTime: Temporal.PlainTime.from(work.from),
      toTime: Temporal.PlainTime.from(work.to),
    }))
    .sort((a, b) => {
      const comparison = Temporal.PlainTime.compare(a.fromTime, b.fromTime)
      if (comparison !== 0) return comparison
      // 開始時間が同じ場合は、終了時間が遅い方を先に（長い予定を下のレーンに）
      return Temporal.PlainTime.compare(b.toTime, a.toTime)
    })
    .map(({ work }) => work)

  // 各レーンの最後の予定の終了時間を記録
  const laneEndTimes: string[] = []

  // まずレーン番号だけ割り当てる
  const worksWithLanes: Array<T & WorkWithLane> = sortedWorks.map((work) => {
    // 配置可能な最初のレーンを探す
    let lane = 0
    let foundLane = false
    for (const [i, laneEndTime] of laneEndTimes.entries()) {
      // このレーンの最後の予定と重複しないか確認
      if (work.from >= laneEndTime) {
        lane = i
        foundLane = true
        break
      }
    }

    // 既存のレーンに配置できない場合は新しいレーンを作成
    if (!foundLane) {
      lane = laneEndTimes.length
    }

    // レーンの終了時間を更新
    laneEndTimes[lane] = work.to

    return {
      ...work,
      lane,
    }
  })

  // 各予定が属する時間帯で必要なレーン数（重複する予定の最大レーン番号 + 1）を計算
  return worksWithLanes.map((work) => {
    const overlappingWorks = worksWithLanes.filter((w) => w.to > work.from && w.from < work.to)
    const maxOverlapLane = overlappingWorks.length > 0 ? Math.max(...overlappingWorks.map((w) => w.lane)) : 0

    return {
      ...work,
      maxOverlapCount: maxOverlapLane + 1,
    }
  })
}
